/**
 * Phase 13 Beat 3 — 2-step Application Confirm Dialog.
 * Step 1: preview → user clicks "Start & Open Application" (POSTs /applications/start,
 *          opens external URL in a new tab, moves to Step 2).
 * Step 2: return-and-confirm → user confirms whether the external submission
 *          actually completed. Only "Submitted" here counts as a successful
 *          application. Cancel records it as cancelled.
 */
import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  XCircle, PaperPlaneTilt, ArrowSquareOut, CheckCircle, Warning,
  ArrowLeft, Question,
} from "@phosphor-icons/react";

const BAND_TONE = {
  "Highly Relevant": "bg-emerald-600 text-white",
  "Relevant":        "bg-sky-600 text-white",
  "Consider":        "bg-amber-500 text-white",
  "Low Match":       "bg-slate-500 text-white",
};

const STATUS_TONE = {
  match:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  partial:  "bg-lime-50 text-lime-800 border-lime-200",
  gap:      "bg-amber-50 text-amber-800 border-amber-200",
  mismatch: "bg-red-50 text-red-800 border-red-200",
  unknown:  "bg-slate-100 text-slate-600 border-slate-200",
  na:       "bg-slate-100 text-slate-500 border-slate-200",
};

export default function ApplicationConfirmDialog({ candidateId, job, onClose, onSubmitted }) {
  const [step, setStep] = useState("preview");
  const [busy, setBusy] = useState(false);
  const [application, setApplication] = useState(null);
  const [notes, setNotes] = useState("");
  const [employerRef, setEmployerRef] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [error, setError] = useState(null);

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/applications/start`, {
        candidate_id: candidateId, job_id: job.job_id,
      });
      const url = data.application.application_url || data.application.job_url;
      setApplication(data.application);
      setStep("confirm");
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.warning("No external URL on file — record the application after submitting manually.");
    } catch (ex) {
      const status = ex.response?.status;
      if (status === 409) {
        setError({
          kind: "duplicate",
          msg: ex.response?.data?.detail?.message || "This candidate already applied to this job.",
          existing: ex.response?.data?.detail?.existing,
        });
      } else {
        setError({ kind: "error",
          msg: formatApiError(ex.response?.data?.detail) || "Could not start application" });
      }
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${application.id}/confirm`, {
        notes: notes || null, employer_reference: employerRef || null,
      });
      toast.success("Application marked as submitted.");
      onSubmitted && onSubmitted();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Confirmation failed");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${application.id}/cancel`, {
        reason: cancelReason || "Not submitted",
      });
      toast.info("Application recorded as not submitted.");
      onSubmitted && onSubmitted();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const snapshot = application?.match_snapshot || {
    overall_score: job.overall_score,
    band: job.band,
    review_required: job.review_required,
    review_reasons: job.review_reasons,
    factors: null,
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="application-confirm-dialog">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[92vh] overflow-auto">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900"
                data-testid="application-dialog-title">
              {step === "preview" ? "Apply to Job" : "Confirm Application"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {job.job_title} · {job.company} · {job.location}
            </p>
          </div>
          <button onClick={onClose} data-testid="application-dialog-close"
                   className="text-slate-400 hover:text-slate-700"><XCircle size={20} /></button>
        </div>

        {error?.kind === "duplicate" && (
          <div className="m-5 border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm text-amber-900"
               data-testid="application-duplicate-warning">
            <div className="font-semibold inline-flex items-center gap-1.5">
              <Warning size={14} weight="fill" /> Duplicate Application
            </div>
            <div className="text-xs mt-1">{error.msg}</div>
            {error.existing && (
              <div className="text-xs mt-1 text-amber-800">
                Existing status: <b>{error.existing.status}</b> · Started {new Date(error.existing.started_at).toLocaleString()}
                {error.existing.submitted_by_name && <> · by {error.existing.submitted_by_name}</>}
              </div>
            )}
          </div>
        )}

        {error?.kind === "error" && (
          <div className="m-5 border border-red-200 bg-red-50 rounded p-3 text-sm text-red-700"
               data-testid="application-error">{error.msg}</div>
        )}

        <div className="p-5 space-y-4">
          {/* Match snapshot */}
          <div className="border border-slate-200 rounded-lg p-4"
               data-testid="application-match-snapshot">
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-16 shrink-0">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none" stroke="#e2e8f0" strokeWidth="3" />
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none" stroke="#4f46e5" strokeWidth="3"
                        strokeDasharray={`${snapshot.overall_score}, 100`} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-slate-900">
                  {snapshot.overall_score}%
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${BAND_TONE[snapshot.band] || "bg-slate-500 text-white"}`}
                        data-testid="application-match-band">
                    {snapshot.band}
                  </span>
                  {snapshot.review_required && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                      <Warning size={10} weight="fill" /> Review Required
                    </span>
                  )}
                </div>
                {snapshot.review_required && snapshot.review_reasons?.length > 0 && (
                  <ul className="mt-1.5 list-disc list-inside text-[11px] text-amber-800 space-y-0.5"
                      data-testid="application-review-reasons">
                    {snapshot.review_reasons.map((r) => <li key={`ar-${r}`}>{r}</li>)}
                  </ul>
                )}
                {snapshot.factors && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(snapshot.factors).map(([k, f]) => (
                      <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_TONE[f.status] || STATUS_TONE.unknown}`}>
                        {k}: {f.status}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Step-specific body */}
          {step === "preview" && (
            <div>
              <p className="text-sm text-slate-700">
                Clicking <b>Start & Open Application</b> will:
              </p>
              <ol className="mt-2 text-sm text-slate-700 list-decimal list-inside space-y-1">
                <li>Freeze this match score + JD for historical record.</li>
                <li>Open the external application page in a new tab.</li>
                <li>Come back here to confirm whether the submission completed.</li>
              </ol>
              <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
                <b>Reminder:</b> This click does <u>not</u> count as a submitted application on its own.
                Only after you confirm below does the record become successful.
              </div>
            </div>
          )}

          {step === "confirm" && application && (
            <div className="space-y-3">
              <div className="bg-sky-50 border border-sky-200 rounded p-3 text-sm text-sky-900">
                External application opened. Return here after submitting and confirm below.
              </div>
              {job.application_url || job.job_url ? (
                <a href={application.application_url || application.job_url} target="_blank" rel="noreferrer"
                   data-testid="application-external-link"
                   className="inline-flex items-center gap-1.5 text-xs text-indigo-700 hover:underline">
                  <ArrowSquareOut size={12} /> Reopen external application
                </a>
              ) : null}

              <label className="block">
                <div className="text-xs font-medium text-slate-600 mb-1">Employer reference / applicant ID (optional)</div>
                <input value={employerRef} onChange={(e) => setEmployerRef(e.target.value)}
                       data-testid="application-employer-ref"
                       placeholder="e.g. Confirmation number from the employer portal"
                       className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </label>

              <label className="block">
                <div className="text-xs font-medium text-slate-600 mb-1">Notes (optional)</div>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                          data-testid="application-notes"
                          placeholder="Anything the team should know about this submission"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </label>

              <details className="border border-slate-200 rounded p-2">
                <summary className="text-xs text-slate-600 cursor-pointer">Did you have to abandon this application?</summary>
                <div className="mt-2 space-y-2">
                  <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                         data-testid="application-cancel-reason"
                         placeholder="Reason (e.g. role closed, employer rejected upfront)"
                         className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                  <button onClick={cancel} disabled={busy}
                           data-testid="application-cancel-btn"
                           className="text-xs px-3 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50">
                    Record as not submitted
                  </button>
                </div>
              </details>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2">
          {step === "preview" ? (
            <>
              <button onClick={onClose}
                       className="text-sm px-4 py-2 rounded border border-slate-200 text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={start} disabled={busy || error?.kind === "duplicate"}
                       data-testid="application-start-btn"
                       className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
                <PaperPlaneTilt size={13} weight="fill" />
                {busy ? "Starting…" : "Start & Open Application"}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep("preview")}
                       className="text-sm px-4 py-2 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
                <ArrowLeft size={13} /> Back
              </button>
              <button onClick={confirm} disabled={busy}
                       data-testid="application-confirm-btn"
                       className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                <CheckCircle size={13} weight="fill" />
                {busy ? "Confirming…" : "Confirm Submission"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
